import { Atom, atom } from 'jotai';

import {
  ChartSelection,
  DeviceAttribute,
  TrendChart,
  TrendGroup,
} from '@/features/clickhouse-trends/trends.types';

import {
  chartAttributesAtom,
  chartReadingsAtom,
  chartSelectionAtom,
  chartSettingsAtom,
} from './chart.state';
import { chartAtomFamily, trendGroupsAtom } from './trend-groups.state';

/**
 * Atom storing the ID of the currently active chart.
 * Used to identify which chart is being viewed/edited.
 *
 * A lot of the other atoms build off of changes to this atom.
 * When this atom changes, the currentChartAtom automatically updates to fill itself with the current chart.
 * From there a cascade of child atoms update.
 */
export const currentChartIdAtom = atom<string | null>(null);
currentChartIdAtom.debugLabel = 'currentChartIdAtom';

/**
 * Derived atom that automatically resolves to the full TrendChart object
 * based on the currentChartIdAtom. Searches through all groups' charts to find a match.
 * Also provides a write interface to update the chart within its parent group.
 *
 * This write interface is important because it means when we update the current chart,
 * we are also updating the record in the trend group atom.
 *
 * Think of it like this.  TrendGroupAtom is a list of charts.  At any given time we are interfacing with the current chart.
 * If we update the current chart we want that update to also happen in the list of charts.
 */
export const currentChartAtom = atom(
  (get) => {
    const charts: TrendChart[] = get(trendGroupsAtom).flatMap((group: TrendGroup) => group.charts);
    const currentChartId = get(currentChartIdAtom);

    return charts.find((chart) => chart.id === currentChartId) ?? null;
  },
  (get, set, updated: TrendChart) => {
    const currentChartId = get(currentChartIdAtom);

    if (!currentChartId) return;

    set(chartAtomFamily(updated.id), updated);
  }
);
currentChartAtom.debugLabel = 'currentChartAtom';

/**
 * Derived atom that provides read/write access to the current chart's expanded state.
 * Automatically extracts the isExpanded property from the current chart and provides
 * a setter to update it within the chart state.
 *
 * Update Cascade:
 * Updating the current chart expanded state
 *  -> updates the current chart atom
 *    -> updates the trend group atom
 */
export const currentChartIsExpandedAtom = atom(
  (get) => {
    const chart = get(currentChartAtom);
    return chart?.isExpanded ?? false;
  },
  (get, set, updated: boolean) => {
    const chart = get(currentChartAtom);
    if (!chart) return;
    set(currentChartAtom, { ...chart, isExpanded: updated });
  }
);

/**
 * Derived atom that provides read/write access to the current chart's selection.
 * Automatically extracts the selection from the current chart and provides
 * a setter to update it within the chart state.
 *
 * This delegates to chartSelectionAtom with the current chart ID.
 *
 * Update Cascade:
 * Updating the current chart selection
 *  -> updates the current chart atom
 *    -> updates the trend group atom
 */
export const currentChartSelectionAtom = atom(
  (get) => {
    const currentChartId = get(currentChartIdAtom);
    if (!currentChartId) return undefined;
    return get(chartSelectionAtom(currentChartId));
  },
  (get, set, updated: ChartSelection) => {
    const currentChartId = get(currentChartIdAtom);
    if (!currentChartId) return;

    set(chartSelectionAtom(currentChartId), updated);
  }
);
currentChartSelectionAtom.debugLabel = 'currentChartSelectionAtom';

/**
 * Derived atom that provides read/write access to device attributes in the current chart's selection.
 * Returns an empty array if no selection exists. Updates propagate to the parent selection.
 *
 * This delegates to chartAttributesAtom with the current chart ID.
 *
 * Update Cascade:
 * Updating the current chart attributes
 *  -> updates the current chart selection atom
 *    -> updates the current chart atom
 *      -> updates the trend group atom
 */
export const currentChartAttributesAtom = atom(
  (get) => {
    const currentChartId = get(currentChartIdAtom);
    if (!currentChartId) return [];
    return get(chartAttributesAtom(currentChartId));
  },
  (get, set, updated: DeviceAttribute[]) => {
    const currentChartId = get(currentChartIdAtom);
    if (!currentChartId) return;

    set(chartAttributesAtom(currentChartId), updated);
  }
);
currentChartAttributesAtom.debugLabel = 'currentChartAttributesAtom';

/**
 * Derived atom that filters current chart attributes to only include settings.
 * Settings are identified by having a namespace of Namespace.Setting.
 *
 * This delegates to chartSettingsAtom with the current chart ID.
 *
 * This is a read only atom.  (you can tell because it doesn't have a set function)
 */
export const currentChartSettingsAtom = atom((get) => {
  const currentChartId = get(currentChartIdAtom);
  if (!currentChartId) return [];
  return get(chartSettingsAtom(currentChartId));
});
currentChartSettingsAtom.debugLabel = 'currentChartSettingsAtom';

/**
 * Derived atom that filters current chart attributes to only include readings.
 * Readings are any attributes that are NOT settings (i.e., different namespace).
 *
 * This delegates to chartReadingsAtom with the current chart ID.
 *
 * This is a read only atom.  (you can tell because it doesn't have a set function)
 */
export const currentChartReadingsAtom = atom((get) => {
  const currentChartId = get(currentChartIdAtom);
  if (!currentChartId) return [];
  return get(chartReadingsAtom(currentChartId));
});
currentChartReadingsAtom.debugLabel = 'currentChartReadingsAtom';

/**
 * IMPORTANT:  MUST BE USED WITH useMemo if used inside the react render loop because
 * it's a function that returns an atom which is not a stable reference
 *
 * This is a read-only atom.
 *
 * Usage:
 * ```typescript
 * const isCurrentChart = useAtomValue(useMemo(() => isCurrentChartAtom(chartId), [chartId]));
 * ```
 */
export const isCurrentChartAtom = (chartId: string): Atom<boolean> =>
  atom((get) => {
    const currentChartId = get(currentChartIdAtom);
    if (!currentChartId) return false;

    return currentChartId === chartId;
  });
